import { useEffect, useState } from "react";
import type { GenericTableRow } from "./types";
import { toComparableString } from "./types";

export interface UseGenericTableStateParams<Row extends GenericTableRow = GenericTableRow> {
  rows: Row[];
  colHeaders: string[];
  customSortFunctions: {
    [key: string]: (a: Row, b: Row, direction: "asc" | "desc") => number;
  };
  selectedRowIds: (string | number)[];
  onSelectionChange: (selectedRowIds: (string | number)[]) => void;
  nonDeselectableRowIds?: (string | number)[];
}

export interface UseGenericTableStateResult<Row extends GenericTableRow = GenericTableRow> {
  searchString: string;
  sortedColumn: string | null;
  sortDirection: "asc" | "desc";
  displayRows: Row[];
  handleSearch: (search: string) => void;
  handleSort: (header: string) => void;
  handleRowCheckboxChange: (rowId: string | number) => void;
  handleCheckAllChange: () => void;
}

export const useGenericTableState = <Row extends GenericTableRow = GenericTableRow>({
  rows,
  colHeaders,
  customSortFunctions,
  selectedRowIds,
  onSelectionChange,
  nonDeselectableRowIds,
}: UseGenericTableStateParams<Row>): UseGenericTableStateResult<Row> => {
  const [searchString, setSearchString] = useState("");
  const [sortedColumn, setSortedColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [displayRows, setDisplayRows] = useState<Row[]>(rows);

  const reapplySort = (rowsToSort: Row[], sortCol: string | null, sortDir: "asc" | "desc") => {
    let sortedRows = rowsToSort;
    if (sortCol) {
      sortedRows = [...rowsToSort].sort((a, b) => {
        const customSortFunction = customSortFunctions[sortCol];
        if (customSortFunction) {
          return customSortFunction(a, b, sortDir);
        }
        const aVal = a[sortCol];
        const bVal = b[sortCol];
        if (typeof aVal === "number" && typeof bVal === "number") {
          return sortDir === "asc" ? aVal - bVal : bVal - aVal;
        }
        const aStr = aVal ? toComparableString(aVal).toLowerCase() : "";
        const bStr = bVal ? toComparableString(bVal).toLowerCase() : "";
        if (aStr < bStr) return sortDir === "asc" ? -1 : 1;
        if (aStr > bStr) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortedRows;
  };

  useEffect(() => {
    let filteredRows = rows;
    if (searchString) {
      filteredRows = rows.filter((row) =>
        colHeaders.some((header) => {
          const cellValue = row[header];
          return (
            cellValue &&
            toComparableString(cellValue).toLowerCase().includes(searchString.toLowerCase())
          );
        }),
      );
    }
    const sorted = reapplySort(filteredRows, sortedColumn, sortDirection);
    setDisplayRows(sorted);
  }, [rows, searchString, sortedColumn, sortDirection, colHeaders, selectedRowIds]);

  const handleSearch = (search: string) => {
    setSearchString(search);
  };

  const handleSort = (header: string) => {
    let newDir: "asc" | "desc" = "asc";
    if (sortedColumn === header) {
      newDir = sortDirection === "asc" ? "desc" : "asc";
    }
    setSortedColumn(header);
    setSortDirection(newDir);
    console.log(`Sorting by ${header} in ${newDir} order.`);
  };

  const handleRowCheckboxChange = (rowId: string | number) => {
    if (nonDeselectableRowIds && nonDeselectableRowIds.includes(rowId)) {
      if (!selectedRowIds.includes(rowId)) {
        onSelectionChange([...selectedRowIds, rowId]);
      }
      return;
    }
    let newSelection: (string | number)[];
    if (selectedRowIds.includes(rowId)) {
      newSelection = selectedRowIds.filter((id) => id !== rowId);
    } else {
      newSelection = [...selectedRowIds, rowId];
    }
    console.log("Row clicked. New selection state:", newSelection);
    onSelectionChange(newSelection);
  };

  const handleCheckAllChange = () => {
    let newSelection: (string | number)[];
    const selectableRows = displayRows.filter(
      (row) => !nonDeselectableRowIds || !nonDeselectableRowIds.includes(row.id),
    );
    const allSelectableSelected =
      selectableRows.length > 0 && selectableRows.every((row) => selectedRowIds.includes(row.id));
    if (allSelectableSelected) {
      newSelection = selectedRowIds.filter((id) =>
        displayRows.some(
          (row) => row.id === id && nonDeselectableRowIds && nonDeselectableRowIds.includes(id),
        ),
      );
    } else {
      const selectableIds = selectableRows.map((row) => row.id);
      newSelection = Array.from(new Set([...selectedRowIds, ...selectableIds]));
    }
    console.log("Check All clicked. New selection state:", newSelection);
    onSelectionChange(newSelection);
  };

  return {
    searchString,
    sortedColumn,
    sortDirection,
    displayRows,
    handleSearch,
    handleSort,
    handleRowCheckboxChange,
    handleCheckAllChange,
  };
};
